import cmsLabelsIcon from "./assets/cms-labels-icon.svg";
import {
  createEmptyTranslations,
  createEmptyHome,
  createEmptyAbout,
  createEmptySolutions,
  createEmptyContact,
} from "./defaults/emptyCmsDefaults";
import {
  TRUST_BAR_IDS,
  FEATURE_IDS,
  STEP_IDS,
  ROLE_IDS,
  ABOUT_FEATURE_IDS,
  SOLUTIONS_SMART_IDS,
  SOLUTIONS_ALT_IDS,
  DEVICE_IDS,
  CONTACT_INFO_IDS,
  CONTACT_FIELD_IDS,
} from "./defaults/cmsIds";
import cmsHomeIcon from "./assets/cms-home-icon.svg";
import cmsAboutIcon from "./assets/cms-about-icon.svg";
import cmsSolutionsIcon from "./assets/cms-solutions-icon.svg";
import cmsContactIcon from "./assets/cms-contact-icon.svg";
import {
  createSeoTranslationKeys,
  createSeoTranslationSection,
  NOT_FOUND_PAGE_KEYS,
} from "./defaults/translationSectionHelpers";

/** When false, technical field keys (e.g. hero.backgroundImage) are hidden in the admin UI. */
export const CMS_SHOW_FIELD_KEYS = false;

export const CMS_LANGUAGES = {
  en: {
    id: "6679591e3a03ae96213a19e8",
    code: "en",
    label: "English",
    flag: "🇺🇸",
  },
  es: {
    id: "667959693a03ae96213a19e9",
    code: "es",
    label: "Español",
    flag: "🇪🇸",
  },
};

/** Tab navigation + page header copy (single source of truth). */
export const CMS_PAGE_META = {
  common: {
    tabTitle: "Common",
    tabDescription: "Nav, footer & SEO",
    tabIcon: cmsLabelsIcon,
    title: "Common labels & SEO",
    subtitle:
      "Edit navigation, footer, brand images, 404 page copy, and SEO meta tags. Changes publish live for English and Spanish.",
    pageIcon: "mdi-translate",
  },
  home: {
    tabTitle: "Home",
    tabDescription: "Landing page",
    tabIcon: cmsHomeIcon,
    title: "Home page",
    subtitle:
      "Hero, trust bar, features, how it works, roles, and security sections. Saves publish live (no draft).",
    pageIcon: "mdi-home-outline",
  },
  about: {
    tabTitle: "About",
    tabDescription: "Mission & story",
    tabIcon: cmsAboutIcon,
    title: "About page",
    subtitle:
      "Hero, feature blocks, and program call-to-action. Saves publish live for English and Spanish.",
    pageIcon: "mdi-information-outline",
  },
  solutions: {
    tabTitle: "Solutions",
    tabDescription: "RPM & devices",
    tabIcon: cmsSolutionsIcon,
    title: "Solutions page",
    subtitle:
      "Smart solutions grid, alternating features, integrated devices, and CTA. Saves publish live.",
    pageIcon: "mdi-lightbulb-on-outline",
  },
  contact: {
    tabTitle: "Contact",
    tabDescription: "Form & info cards",
    tabIcon: cmsContactIcon,
    title: "Contact page",
    subtitle:
      "Hero, contact info cards, and message form labels. Saves publish live for English and Spanish.",
    pageIcon: "mdi-email-outline",
  },
};

export const CMS_WEBSITE_TABS = Object.entries(CMS_PAGE_META).map(([id, meta]) => ({
  id,
  label: meta.tabTitle,
  image: meta.tabIcon,
  description: meta.tabDescription,
}));

export {
  TRUST_BAR_IDS,
  FEATURE_IDS,
  STEP_IDS,
  ROLE_IDS,
  ABOUT_FEATURE_IDS,
  SOLUTIONS_SMART_IDS,
  SOLUTIONS_ALT_IDS,
  DEVICE_IDS,
  CONTACT_INFO_IDS,
  CONTACT_FIELD_IDS,
} from "./defaults/cmsIds";

export const HOME_SECTIONS = [
  { id: "hero", label: "Hero", hint: "Top banner" },
  { id: "trustBar", label: "Trust bar", hint: "Scrolling trust icons" },
  { id: "careChallenge", label: "Care challenge", hint: "Two text blocks + bridge" },
  { id: "features", label: "Key features", hint: "9 feature cards" },
  { id: "howItWorks", label: "How it works", hint: "Image + 4 steps" },
  { id: "roleExperience", label: "Role experience", hint: "4 role cards" },
  { id: "security", label: "Security & CTA", hint: "Compliance + bottom CTA" },
];

export const TRANSLATION_SECTIONS = [
  {
    id: "site",
    title: "Site",
    description: "Site name and logo accessibility text.",
    keys: [
      { key: "site.name", label: "Site name", maxLength: 200 },
      { key: "brand.logo.alt", label: "Logo alt text (optional)", maxLength: 200 },
      {
        key: "brand.logo.src",
        label: "Footer / app promo logo",
        maxLength: 500,
        kind: "imagePath",
      },
      {
        key: "brand.headerLogo.src",
        label: "Header logo",
        maxLength: 500,
        kind: "imagePath",
      },
      {
        key: "footer.backgroundImage",
        label: "Footer background image",
        maxLength: 500,
        kind: "imagePath",
      },
    ],
  },
  {
    id: "seo",
    title: "SEO — Home page",
    description:
      "Published together with navigation and footer labels. Updates browser tab, meta tags, and social previews.",
    keys: createSeoTranslationKeys("seo.home"),
  },
  createSeoTranslationSection("seoAbout", "SEO — About page", "Meta tags for /about", "seo.about"),
  createSeoTranslationSection(
    "seoSolutions",
    "SEO — Solutions page",
    "Meta tags for /solutions",
    "seo.solutions"
  ),
  createSeoTranslationSection(
    "seoContact",
    "SEO — Contact page",
    "Meta tags for /contact",
    "seo.contact"
  ),
  {
    id: "navigation",
    title: "Navigation",
    description: "Main navigation link labels (URLs are fixed in website code).",
    keys: [
      { key: "nav.home", label: "Home (navigation)", maxLength: 200 },
      { key: "nav.about", label: "About (navigation)", maxLength: 200 },
      { key: "nav.solutions", label: "Solutions (navigation)", maxLength: 200 },
      { key: "nav.contact", label: "Contact (navigation)", maxLength: 200 },
      { key: "nav.login", label: "Login (header CTA)", maxLength: 200 },
    ],
  },
  {
    id: "footerHeadings",
    title: "Footer — headings",
    description: "Footer section titles and copyright line.",
    keys: [
      { key: "footer.tagline", label: "Tagline", maxLength: 500 },
      { key: "footer.quickLinksTitle", label: "Quick links title", maxLength: 200 },
      { key: "footer.supportTitle", label: "Support title", maxLength: 200 },
      { key: "footer.contactTitle", label: "Contact title", maxLength: 200 },
      { key: "footer.copyright", label: "Copyright", maxLength: 500 },
    ],
  },
  {
    id: "footerContact",
    title: "Footer — contact",
    description: "Contact details shown in the footer.",
    keys: [
      { key: "footer.contact.email", label: "Email", type: "email", maxLength: 200 },
      { key: "footer.contact.addressLine1", label: "Address line 1", maxLength: 500 },
      { key: "footer.contact.addressLine2", label: "Address line 2", maxLength: 500 },
      { key: "footer.contact.office", label: "Office phone", maxLength: 200 },
      { key: "footer.contact.fax", label: "Fax", maxLength: 200 },
    ],
  },
  {
    id: "footerLinks",
    title: "Footer — link labels",
    description: "Support column link text (policy URLs are fixed in website code).",
    keys: [
      { key: "footer.link.contact", label: "Contact link", maxLength: 200 },
      { key: "footer.link.privacy", label: "Privacy policy link", maxLength: 200 },
      { key: "footer.link.hipaa", label: "HIPAA policy link", maxLength: 200 },
    ],
  },
  {
    id: "notFound",
    title: "404 — Page not found",
    description:
      "On-screen copy and SEO meta tags when a visitor opens a URL that does not exist (not indexed by search engines).",
    keys: [...NOT_FOUND_PAGE_KEYS, ...createSeoTranslationKeys("seo.notFound")],
  },
];

export const ALL_TRANSLATION_KEYS = TRANSLATION_SECTIONS.reduce(
  (keys, section) => keys.concat(section.keys.map((k) => k.key)),
  []
);

export const DEFAULT_TRANSLATIONS = {
  [CMS_LANGUAGES.en.id]: createEmptyTranslations(ALL_TRANSLATION_KEYS),
  [CMS_LANGUAGES.es.id]: createEmptyTranslations(ALL_TRANSLATION_KEYS),
};

export const DEFAULT_HOME = createEmptyHome();
export const DEFAULT_ABOUT = createEmptyAbout();
export const DEFAULT_SOLUTIONS = createEmptySolutions();
export const DEFAULT_CONTACT = createEmptyContact();

export const MAX_TRANSLATION_VALUE_LENGTH = 500;
